import { inject, Injectable } from '@angular/core';
import { FormControl, NonNullableFormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Employee } from '@shared/models';
import { EmployeeFormGroup, EmployeeFormMode } from '@ep/shared';
import {
  DeviceAdminOption,
  EmployeeAccessTypeOption,
  mapEmployeeAccessTypeToOption,
} from '@app/areas/merchants/utils/employee-access-type-options';

@Injectable()
export class EmployeeFormService {
  private readonly fb = inject(NonNullableFormBuilder);

  private readonly mode$$ = new BehaviorSubject<EmployeeFormMode>('add');
  readonly mode$ = this.mode$$.asObservable();

  private readonly employeeForm: EmployeeFormGroup<EmployeeAccessTypeOption> = this.fb.group({
    Details: this.fb.group({
      NameFirst: this.fb.control<string | null>(null, { validators: [Validators.required] }),
      NameLast: this.fb.control<string | null>(null, { validators: [Validators.required] }),
      EmailAddress: this.fb.control<string | null>(null, { validators: [Validators.required, Validators.email] }),
      PhoneNumber: this.fb.control<string | null>(null, { validators: [Validators.required] }),
    }),
    Access: this.fb.group({
      AccessType: this.fb.control<EmployeeAccessTypeOption>(DeviceAdminOption, { validators: [Validators.required] }),
    }),
    Terminal: this.fb.group({
      Pins: this.fb.array<FormControl<string>>([]),
    }),
  });

  getForm(): EmployeeFormGroup<EmployeeAccessTypeOption> {
    return this.employeeForm;
  }

  getMode(): EmployeeFormMode {
    return this.mode$$.value;
  }

  initializeForAdd(): void {
    this.mode$$.next('add');
    this.resetTo(null);
    this.employeeForm.enable();
  }

  initializeForView(employee: Employee): void {
    this.mode$$.next('view');
    this.resetTo(employee);
    this.employeeForm.disable();
  }

  initializeForEdit(employee: Employee): void {
    this.mode$$.next('edit');
    this.resetTo(employee);
    this.employeeForm.enable();
  }

  addTerminalPin(value = ''): void {
    this.employeeForm.controls.Terminal.controls.Pins.push(this.fb.control<string>(value));
  }

  removeTerminalPin(): void {
    this.employeeForm.controls.Terminal.controls.Pins.clear();
  }

  private resetTo(employee: Employee | null): void {
    this.employeeForm.reset({
      Details: {
        NameFirst: employee?.NameFirst ?? null,
        NameLast: employee?.NameLast ?? null,
        EmailAddress: employee?.EmailAddress ?? null,
        PhoneNumber: employee?.PhoneNumber ?? null,
      },
      Access: {
        AccessType: mapEmployeeAccessTypeToOption(employee?.AccessType),
      },
      Terminal: {
        Pins: [],
      },
    });
    this.employeeForm.controls.Terminal.controls.Pins.clear({ emitEvent: false });
    if (employee?.Pin) {
      this.addTerminalPin(employee.Pin);
    }
  }
}
